import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DomainInput, EntryInput, EntryUpdateInput } from '@redirector/shared';
import { apiGet, apiSend } from '../lib/api.js';
import type { Domain } from '../groups/queries.js';
import { groupKeys } from '../groups/queries.js';
import type { EntrySummary } from '../domains/queries.js';
import { entryKeys } from '../domains/queries.js';

/** One row of `GET /api/urls`. Mirrors the endpoint exactly. */
export type UrlRow = {
  entryId: number;
  fullUrl: string;
  path: string;
  pathNorm: string;
  label: string;
  enabled: boolean;
  domainId: number;
  hostname: string;
  groupId: number;
  groupName: string;
  targetCount: number;
  enabledTargetCount: number;
  lastSeenAt: string | null;
  /**
   * Domain-scoped, not entry-scoped: a miss means nothing matched, so there is
   * no entry to attribute it to. Two URLs on one hostname report the same
   * value, which is why the column says so in its header.
   */
  lastMissAt: string | null;
};

/** A domain as the liveness projection reports it — every domain, with its group. */
export type DomainWithGroup = {
  domainId: number;
  hostname: string;
  groupId: number;
  groupName: string;
  enabled: boolean;
  entryCount: number;
};

export const urlKeys = {
  all: ['urls'] as const,
  list: (groupId?: number) => ['urls', groupId ?? 'all'] as const,
};

export function useUrls(groupId?: number) {
  return useQuery({
    queryKey: urlKeys.list(groupId),
    queryFn: () =>
      apiGet<{ urls: UrlRow[] }>(
        groupId === undefined ? '/api/urls' : `/api/urls?groupId=${groupId}`,
      ),
  });
}

/**
 * Every domain, for detecting that a pasted hostname is already configured.
 *
 * `GET /api/urls` cannot answer this: a domain with no entries yet has no rows
 * there, and creating the first URL on a freshly added hostname is exactly the
 * case that must not create a second domain. The liveness projection is the
 * only endpoint that lists domains across groups, and it already carries the
 * group each one belongs to. Its `['liveness']` key is the one the existing
 * domain and entry mutations invalidate, so reuse detection stays current for
 * free.
 */
export function useDomainsAcrossGroups() {
  return useQuery({
    queryKey: ['liveness'],
    queryFn: () => apiGet<{ domains: DomainWithGroup[] }>('/api/reports/liveness'),
  });
}

/** Drops the flat listing for every filter, since a new URL can join any of them. */
export function useInvalidateUrls() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: urlKeys.all });
  };
}

export type CreateUrlInput = {
  /** The domain to hang the entry off, when the hostname already exists. */
  domainId?: number;
  /** The group to create the domain under, when the hostname is new. */
  groupId?: number;
  hostname: string;
  entry: EntryInput;
};

/**
 * Creates a URL, adding its domain first if the hostname is new.
 *
 * Not composed from `useCreateEntry`: that hook is parameterised by a domain id
 * at render time, and for a new hostname the id does not exist until the domain
 * has been created inside this same submit. So the two calls live in one
 * mutation function, which also makes the ordering — domain, then entry —
 * explicit rather than spread across two renders.
 *
 * The domain is NOT rolled back if the entry then fails. A domain with no
 * entries is inert (traffic to it falls through to the fallback) and the
 * operator can retry the URL against it; deleting it would instead destroy a
 * hostname that may already be pointed at us.
 */
export function useCreateUrl() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateUrlInput) => {
      let domainId = input.domainId;

      if (domainId === undefined) {
        if (input.groupId === undefined) throw new Error('Choose a group for the new domain');
        const domainInput: DomainInput = {
          hostname: input.hostname,
          fallbackUrl: null,
          enabled: true,
        };
        const created = await apiSend<{ domain: Domain }>(
          'POST',
          `/api/groups/${input.groupId}/domains`,
          domainInput,
        );
        domainId = created.domain.id;
      }

      const entry = await apiSend<{ entry: EntrySummary }>(
        'POST',
        `/api/domains/${domainId}/entries`,
        input.entry,
      );
      return { domainId, entry: entry.entry };
    },
    onSuccess: (result, input) => {
      void client.invalidateQueries({ queryKey: urlKeys.all });
      void client.invalidateQueries({ queryKey: groupKeys.all });
      void client.invalidateQueries({ queryKey: ['liveness'] });
      void client.invalidateQueries({
        queryKey: entryKeys.forDomain(result.domainId),
      });
      if (input.groupId !== undefined) {
        void client.invalidateQueries({
          queryKey: groupKeys.one(input.groupId),
        });
      }
    },
  });
}

/**
 * Flips one URL's enabled flag, from wherever it is displayed.
 *
 * `PATCH /api/entries/:id` replaces the whole entry, so the label and path
 * must be sent back unchanged alongside the new flag — they come from the row
 * or page that owns the toggle, which already has them on screen.
 *
 * Not composed from `useUpdateEntry`: that hook is parameterised by a domain
 * id at render time, and the URLs listing shows many domains at once. The
 * domain id travels with the call instead, so the same hook serves one row of
 * eight hundred and the single URL page.
 */
export function useSetUrlEnabled() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (variables: {
      entryId: number;
      domainId: number;
      label: string;
      path: string;
      enabled: boolean;
    }) => {
      const input: EntryInput = {
        label: variables.label,
        path: variables.path,
        enabled: variables.enabled,
      };
      return apiSend<{ entry: EntrySummary }>('PATCH', `/api/entries/${variables.entryId}`, input);
    },
    onSuccess: (_data, variables) => {
      void client.invalidateQueries({ queryKey: urlKeys.all });
      void client.invalidateQueries({
        queryKey: entryKeys.one(variables.entryId),
      });
      void client.invalidateQueries({
        queryKey: entryKeys.forDomain(variables.domainId),
      });
      void client.invalidateQueries({ queryKey: ['liveness'] });
    },
  });
}

/**
 * Everything one row's change can make stale, in one place.
 *
 * The listing shows many domains at once, so the domain id travels with each
 * call rather than being baked in at render time — the same reason
 * `useSetUrlEnabled` is shaped this way. A hostname edit can also move the
 * entry OFF `domainId`, which is why the flat URLs listing and the liveness
 * projection are dropped wholesale: the domain it moved to is not knowable
 * from here.
 */
function useAfterRowChange() {
  const client = useQueryClient();
  return (variables: { entryId: number; domainId: number }) => {
    void client.invalidateQueries({ queryKey: urlKeys.all });
    void client.invalidateQueries({ queryKey: entryKeys.one(variables.entryId) });
    void client.invalidateQueries({ queryKey: entryKeys.forDomain(variables.domainId) });
    void client.invalidateQueries({ queryKey: ['liveness'] });
  };
}

/** Edits one URL from the listing, hostname included. */
export function useUpdateUrl() {
  const after = useAfterRowChange();
  return useMutation({
    mutationFn: (variables: { entryId: number; domainId: number; input: EntryUpdateInput }) =>
      apiSend<{ entry: EntrySummary }>(
        'PATCH',
        `/api/entries/${variables.entryId}`,
        variables.input,
      ),
    onSuccess: (_data, variables) => after(variables),
  });
}

/** Deletes one URL from the listing. Soft on the server: reports keep it. */
export function useDeleteUrl() {
  const after = useAfterRowChange();
  return useMutation({
    mutationFn: (variables: { entryId: number; domainId: number }) =>
      apiSend<{ deleted: true }>('DELETE', `/api/entries/${variables.entryId}`),
    onSuccess: (_data, variables) => after(variables),
  });
}
